import { apiClient } from './apiClient';
import { toast } from 'sonner';

/**
 * Service for invitation-related operations
 * Handles user invitations, role assignments, and superintendent invitations
 */
export const inviteService = {
  /**
   * Invite a superintendent by calling the invite-superintendent edge function
   * This function:
   * 1. Creates user in auth.users with metadata
   * 2. Sends magic link email that goes directly to dashboard
   * 3. Database trigger automatically creates profile
   */
  inviteSuperintendent: async (payload: {
    email: string;
    employee_name: string;
    first_name: string;
    last_name: string;
    department: string;
  }): Promise<{ success: boolean; error?: string; data?: any }> => {
    try {
      console.log("🔄 Inviting superintendent...");
      
      // Validate inputs before sending to Edge Function
      if (!payload.email || !payload.employee_name) {
        return { 
          success: false, 
          error: 'Email and employee name are required' 
        };
      }
      
      if (!payload.department) {
        return { 
          success: false, 
          error: 'Employee must have a department assigned' 
        };
      }
      
      // Use the centralized API client to call the Edge Function
      const { data, error } = await apiClient.invokeEdgeFunction('invite-superintendent', {
        body: payload
      });
      
      if (error) {
        console.error("❌ Superintendent invitation failed:", error);
        
        // Handle specific error cases with better user feedback
        if (error.message?.includes('User already exists') || error.message?.includes('already registered')) {
          return { 
            success: false, 
            error: 'A user with this email already exists. Please use a different email address.' 
          };
        } else if (error.message?.includes('Invalid email') || error.message?.includes('email format')) {
          return { 
            success: false, 
            error: 'Please enter a valid email address.' 
          };
        } else if (error.message?.includes('Invalid or expired token')) {
          return { 
            success: false, 
            error: 'Your session has expired. Please log in again.' 
          };
        } else if (error.message?.includes('Insufficient privileges') || error.message?.includes('insufficient privileges')) {
          return { 
            success: false, 
            error: 'You do not have permission to invite superintendents.' 
          };
        } else if (error.message?.includes('Server configuration error')) {
          return { 
            success: false, 
            error: 'Server configuration issue. Please contact support.' 
          };
        } else if (error.message?.includes('Email service error') || error.message?.includes('Failed to send email')) {
          return { 
            success: false, 
            error: 'Failed to send invitation email. Please try again or contact support.' 
          };
        } else {
          return { 
            success: false, 
            error: `Invitation failed: ${error.message || 'Unknown error'}` 
          };
        }
      }

      if (data?.error) {
        console.error("❌ Invitation returned error:", data.error);
        return { 
          success: false, 
          error: `Invitation failed: ${data.error}` 
        };
      }
      
      console.log("✅ Superintendent invited successfully");
      return { 
        success: true, 
        data: data || { message: 'Invitation sent successfully' } 
      };
      
    } catch (error) {
      console.error("❌ Error inviting superintendent:", error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      return { 
        success: false, 
        error: `Invitation failed: ${errorMessage}` 
      };
    }
  }
};
